import { datadogRum } from "@datadog/browser-rum";
import { consola } from "consola";
import * as v from "valibot";

const ignoredErrorSchema = v.object({
  attributes: v.object({
    source: v.literal("console"),
    stack: v.pipe(
      v.string(),
      v.check((it) => it.includes("https://js-eu1.hubspot.com")),
    ),
  }),
});

function nonEmptyString(fieldName: string) {
  return v.pipe(
    v.string(`dd.${fieldName} must be a string`),
    v.trim(),
    v.nonEmpty(`dd.${fieldName} cannot be empty`),
  );
}

const ddConfigSchema = v.object({
  applicationId: nonEmptyString("applicationId"),
  clientToken: nonEmptyString("clientToken"),
  version: nonEmptyString("version"),
  env: nonEmptyString("env"),
  service: nonEmptyString("service"),
  site: nonEmptyString("site"),
});

interface CookieYesConsentEvent {
  detail: {
    accepted: string[];
    rejected: string[];
  };
}

interface CookieYesConsentData {
  activeLaw: string;
  categories?: {
    necessary: boolean;
    functional: boolean;
    analytics: boolean;
    performance: boolean;
    advertisement: boolean;
  };
  isUserActionCompleted: boolean;
  consentID: string;
  languageCode: string;
}

declare const window: Window & {
  // https://www.cookieyes.com/documentation/retrieving-consent-data-using-api-getckyconsent/
  getCkyConsent?: () => CookieYesConsentData;
};

/**
 * Add integration with CookieYes to update tracking consent if analytics is accepted by the user
 *
 * @see https://www.cookieyes.com/documentation/events-on-cookie-banner-interactions/
 */
function addCookieYesListener() {
  // @ts-expect-error - TS doesn't know about this event
  document.addEventListener("cookieyes_consent_update", (data: CookieYesConsentEvent) => {
    try {
      if (data.detail.accepted.includes("analytics")) {
        datadogRum.setTrackingConsent("granted");
      } else {
        datadogRum.setTrackingConsent("not-granted");
      }
    } catch (error) {
      console.error("Error in cookieyes_consent_update", error);
      datadogRum.setTrackingConsent("not-granted");
    }
  });
}

export default defineNuxtPlugin(
  ({
    $config: {
      public: { datadog, urls },
    },
  }) => {
    const config = v.safeParse(ddConfigSchema, datadog);
    if (!config.success) {
      if (import.meta.dev) {
        // FIXME: This should be reported to an endpoint and logged on the server if it happens in production
        consola.warn("Invalid Datadog configuration", v.summarize(config.issues));
      }
      return;
    }

    const consent = window.getCkyConsent?.();

    const hasConsented = consent?.categories?.analytics;

    datadogRum.init({
      ...config.output,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      trackingConsent: (hasConsented || config.output.env === "test") ? "granted" : "not-granted",
      allowedTracingUrls: [
        window.location.origin,
        ...Object.values(urls.core),
        urls.crystallize,
      ],
      beforeSend(event) {
        return !v.is(ignoredErrorSchema, event.error);
      },
    });

    addCookieYesListener();
  },
);
